import { useEffect, useRef } from "react";

const PORTAL_SELECTORS = [
  "[data-onboarding-coachmark]",
  ".MuiPopover-root",
  ".MuiMenu-root",
  ".MuiModal-root",
  ".MuiDialog-root",
].join(", ");

function isScrollAllowed(target: EventTarget | null, targetIds: string[]): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest(PORTAL_SELECTORS)) return true;
  return targetIds.some((id) => Boolean(target.closest(`[data-onboarding-id="${id}"]`)));
}

/**
 * Lock document scroll while the onboarding overlay is visible so fixed cutouts
 * stay aligned with highlighted targets on touch devices.
 */
export function useOnboardingScrollLock(locked: boolean, targetIds: string[]) {
  const targetIdsRef = useRef(targetIds);
  targetIdsRef.current = targetIds;

  useEffect(() => {
    if (!locked || typeof document === "undefined") return;

    const html = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY;

    const prevHtmlOverflow = html.style.overflow;
    const prevHtmlOverscroll = html.style.overscrollBehavior;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyPosition = body.style.position;
    const prevBodyTop = body.style.top;
    const prevBodyWidth = body.style.width;

    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";

    const onTouchMove = (event: TouchEvent) => {
      if (isScrollAllowed(event.target, targetIdsRef.current)) return;
      event.preventDefault();
    };

    document.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      document.removeEventListener("touchmove", onTouchMove);
      html.style.overflow = prevHtmlOverflow;
      html.style.overscrollBehavior = prevHtmlOverscroll;
      body.style.overflow = prevBodyOverflow;
      body.style.position = prevBodyPosition;
      body.style.top = prevBodyTop;
      body.style.width = prevBodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [locked]);
}
